import React, { FC, useEffect } from 'react';
import { Typography, Space, Select, Button, Spin, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { useBusinessDocumentType } from '~/hooks/useHandbook';
import {
  Form,
  BusinessDocumentParametersInputGroup,
  BusinessDocumentPagesInputGroup,
  BackButton,
} from '~/components';
import { useBusinessPrimaryDocumentUpdate, useCurrentBusinessKyc, useKycPage } from '~/hooks/kyc';
import { useStatefulForm } from '~/hooks/form';
import { formWidth } from './utils';
import { NamePath } from 'antd/lib/form/interface';
import { Business } from '~/types';

const { Title, Text } = Typography;

const PrimaryDocument: FC<any> = (props) => {
  const { className } = props;

  const [t] = useTranslation('kyc');

  const { currentBusiness, isLoading, setCurrentBusiness } = useCurrentBusinessKyc();
  const countryId = currentBusiness?.country;
  const legalType = currentBusiness?.legalType;
  const businessDocumentType = useBusinessDocumentType({
    countryId,
    legalType,
    filterOptions: (item) => !!item.primary,
  });

  const { form, handleFormChange, formState } = useStatefulForm<Business | null>(currentBusiness);

  const primaryDocumentMutation = useBusinessPrimaryDocumentUpdate();

  const kycPage = useKycPage();
  const onFinish = async (values: any) => {
    if (primaryDocumentMutation.ready) {
      const newBusiness = { ...currentBusiness, ...values };
      await primaryDocumentMutation.mutateAsync(values);
      setCurrentBusiness(newBusiness);
      kycPage.goNext();
    }
  };

  const getNamePath = (name: NamePath) => ['primaryDocument', name].flat();

  const documentType = formState?.primaryDocument?.type;
  useEffect(() => {
    const isCurrentDocumentStillPrimary = !!businessDocumentType.options.find(
      ({ value }) => value === documentType
    );
    if (documentType && !isCurrentDocumentStillPrimary) {
      form.resetFields();
      handleFormChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentType]);

  return (
    <Space direction="vertical" size="large" className={cx(className, 'Step-Container')}>
      <div>
        <Title level={3}>{t('Primary company documents')}</Title>
        <Text>{t('Please select type of document and click to upload documents scan(s)')}</Text>
      </div>
      {isLoading || !primaryDocumentMutation.ready ? (
        <Spin />
      ) : (
        <Form
          form={form}
          onValuesChange={(_, newValues) => handleFormChange(newValues)}
          onFinish={onFinish}
          layout="vertical"
        >
          <Row gutter={[rowGap, 0]}>
            <Col flex="none" style={{ width: formWidth.width + rowGap }}>
              <Form.Item
                label={t('Document type')}
                name={getNamePath('type')}
                rules={[{ required: true }]}
              >
                <Select
                  options={businessDocumentType.options}
                  loading={businessDocumentType.isLoading}
                />
              </Form.Item>
              <BusinessDocumentParametersInputGroup
                countryId={countryId}
                legalType={legalType}
                documentType={documentType}
                nameFn={(name) => getNamePath(name)}
              />
            </Col>
            <Col flex="none">
              <BusinessDocumentPagesInputGroup
                countryId={countryId}
                legalType={legalType}
                documentType={documentType}
                nameFn={(fileIndex) => getNamePath(['files', fileIndex])}
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col>
              <BackButton onClick={kycPage.goBack} size="large" />
            </Col>
            <Col>
              <Form.Item noStyle>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={primaryDocumentMutation.isLoading}
                >
                  {t('translation:Next')}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Space>
  );
};

const rowGap = 16;

export default PrimaryDocument;
